import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProjectFilters = ({ filters, onFiltersChange, totalCount, filteredCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'ongoing', label: 'جاري' },
    { value: 'completed', label: 'مكتمل' },
    { value: 'planned', label: 'مخطط' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'جميع الفئات' },
    { value: 'academic', label: 'أكاديمي' },
    { value: 'cultural', label: 'ثقافي' },
    { value: 'social', label: 'اجتماعي' },
    { value: 'technical', label: 'تقني' },
    { value: 'community', label: 'مجتمعي' }
  ];

  const sortOptions = [
    { value: 'title', label: 'العنوان' },
    { value: 'progress', label: 'نسبة التقدم' },
    { value: 'startDate', label: 'تاريخ البداية' },
    { value: 'endDate', label: 'تاريخ الانتهاء' },
    { value: 'memberCount', label: 'عدد الأعضاء' },
    { value: 'createdAt', label: 'تاريخ الإنشاء' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      search: '',
      status: 'all',
      category: 'all',
      progressMin: 0,
      progressMax: 100,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  const hasActiveFilters = filters?.search || filters?.status !== 'all' || filters?.category !== 'all' || 
                          filters?.progressMin > 0 || filters?.progressMax < 100;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6 shadow-elevation-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-foreground">تصفية المشاريع</h2>
          
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span>عرض {filteredCount} من {totalCount} مشروع</span>
            {hasActiveFilters && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                <Icon name="Filter" size={12} className="mr-1" />
                مُرشح
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              iconName="X"
              iconPosition="left"
              iconSize={14}
            >
              مسح الفلاتر
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            iconSize={16}
          >
            {isExpanded ? 'إخفاء' : 'المزيد'}
          </Button>
        </div>
      </div>
      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Input
          type="search"
          placeholder="البحث في المشاريع..."
          value={filters?.search}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />

        <Select
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => handleFilterChange('status', value)}
          placeholder="اختر الحالة"
        />

        <Select
          options={categoryOptions}
          value={filters?.category}
          onChange={(value) => handleFilterChange('category', value)}
          placeholder="اختر الفئة"
        />

        <div className="flex items-center space-x-2">
          <Select
            options={sortOptions}
            value={filters?.sortBy}
            onChange={(value) => handleFilterChange('sortBy', value)}
            placeholder="ترتيب حسب"
          />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleFilterChange('sortOrder', filters?.sortOrder === 'asc' ? 'desc' : 'asc')}
            iconName={filters?.sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'}
            iconSize={16}
            title={filters?.sortOrder === 'asc' ? 'تصاعدي' : 'تنازلي'}
          />
        </div>
      </div>
      {/* Advanced Filters */}
      {isExpanded && (
        <div className="border-t border-border pt-4 animate-slide-up">
          <h3 className="text-sm font-medium text-foreground mb-3">فلاتر متقدمة</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Progress Range */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نطاق التقدم ({filters?.progressMin}% - {filters?.progressMax}%)
              </label>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={filters?.progressMin}
                    onChange={(e) => handleFilterChange('progressMin', parseInt(e?.target?.value) || 0)}
                    placeholder="الحد الأدنى"
                    className="flex-1"
                  />
                  
                  <span className="text-muted-foreground">إلى</span>
                  
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={filters?.progressMax}
                    onChange={(e) => handleFilterChange('progressMax', parseInt(e?.target?.value) || 100)}
                    placeholder="الحد الأقصى"
                    className="flex-1"
                  />
                </div>

                <div className="relative">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters?.progressMin}
                    onChange={(e) => handleFilterChange('progressMin', parseInt(e?.target?.value))}
                    className="absolute w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                  />
                  
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters?.progressMax}
                    onChange={(e) => handleFilterChange('progressMax', parseInt(e?.target?.value))}
                    className="absolute w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نطاق التاريخ
              </label>
              
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="date"
                  value={filters?.startDateFrom || ''}
                  onChange={(e) => handleFilterChange('startDateFrom', e?.target?.value)}
                  placeholder="من تاريخ"
                />
                
                <Input
                  type="date"
                  value={filters?.startDateTo || ''}
                  onChange={(e) => handleFilterChange('startDateTo', e?.target?.value)}
                  placeholder="إلى تاريخ"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Quick Status Filters */}
      <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
        <span className="text-sm text-muted-foreground mr-3">فلترة سريعة:</span>
        
        {statusOptions?.slice(1)?.map((status) => (
          <Button
            key={status?.value}
            variant={filters?.status === status?.value ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleFilterChange('status', status?.value)}
            className="text-xs"
          >
            {status?.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default ProjectFilters;